import { formatDate, formatEuro } from '@brimo/shared';
import type { ContactFinancing } from '../types';

/**
 * Texts and targets of the financing band (Paul feedback 02.11.2026, D-380): pure, so the five states can be
 * tested without rendering. `none` shows nothing.
 */

export type FinancingBandTone = 'calculated' | 'returned' | 'expired' | 'sent';

export interface FinancingBandView {
  tone: FinancingBandTone;
  /** Hero („Finanzierbar bis 607.000 €“) or the band's title. */
  title: string;
  /** Second line; null for `returned` and `sent`. */
  line: string | null;
}

/** „26.01.2027“ of a Berlin calendar day `YYYY-MM-DD` (noon UTC never crosses the day boundary). */
export function dayLabel(day: string): string {
  return formatDate(`${day}T12:00:00Z`);
}

export function financingBandView(financing: ContactFinancing): FinancingBandView | null {
  switch (financing.state) {
    case 'none':
      return null;
    case 'sent':
      return {
        tone: 'sent',
        title: financing.sentAt
          ? `Selbstauskunft versendet am ${formatDate(financing.sentAt)}, noch nicht zurück`
          : 'Selbstauskunft versendet, noch nicht zurück',
        line: null,
      };
    case 'returned':
      return {
        tone: 'returned',
        title: 'Selbstauskunft zurück — Finanzierbarkeit noch nicht berechnet',
        line: financing.signedAt ? `Selbstauskunft vom ${formatDate(financing.signedAt)}` : null,
      };
    case 'expired':
      return {
        tone: 'expired',
        title: financing.calculatedAt
          ? `Berechnung vom ${formatDate(financing.calculatedAt)} abgelaufen`
          : 'Berechnung abgelaufen',
        line:
          financing.volumeUsedCents !== null
            ? `Zuletzt finanzierbar bis ${formatEuro(financing.volumeUsedCents)}${financing.validUntil ? ` · gültig bis ${dayLabel(financing.validUntil)}` : ''}`
            : null,
      };
    case 'calculated': {
      const parts = [
        financing.monthlyRateCents !== null ? `${formatEuro(financing.monthlyRateCents)}/Monat` : null,
        financing.validUntil ? `gültig bis ${dayLabel(financing.validUntil)}` : null,
        financing.signedAt ? `Selbstauskunft vom ${formatDate(financing.signedAt)}` : null,
        financing.volumeComputedCents !== null &&
        financing.volumeUsedCents !== null &&
        financing.volumeUsedCents < financing.volumeComputedCents
          ? `manuell angepasst · rechnerisch ${formatEuro(financing.volumeComputedCents)}`
          : null,
      ];
      return {
        tone: 'calculated',
        title: `Finanzierbar bis ${formatEuro(financing.volumeUsedCents ?? 0)}`,
        line: parts.filter((part): part is string => part !== null).join(' · '),
      };
    }
  }
}

/** „Zur Berechnung“: tab „Volumen“ of the version (Selbstauskunft 10). */
export function calculationHref(contactId: string, financing: ContactFinancing): string | null {
  return financing.versionId
    ? `/contacts/${contactId}/self-disclosure/${financing.versionId}?tab=volume`
    : null;
}

/** „Passende Einheiten“: „Alle Einheiten“ with the budget filter (D-348) = the used volume in whole euros. */
export function matchingUnitsHref(financing: ContactFinancing): string | null {
  if (financing.volumeUsedCents === null) return null;
  return `/properties/units?budget=${Math.floor(financing.volumeUsedCents / 100)}`;
}
